import { Button, ButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import TenantPage from 'components/common/TenantPage';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import { EquipmentColumns } from 'components/common/enhanced/common-headers';
import { useRef, useState } from 'react';
import { PurgeButton } from 'components/helpers/PurgeButton';
import { PurgeLinksButton } from 'components/helpers/PurgeLinksButton';
import { NotOperatorRoles } from '../../../../constants';
import { useAppSelector } from 'app/store';
import { isTechnicianCoordinator } from 'components/helpers/IsTechnicianCoordinator';
import { isTechnician } from 'components/helpers/IsTechnician';
import { isDirector } from 'components/helpers/IsDirector';
import { isSupplier } from 'components/helpers/IsSupplier';
import { Dialog, DialogContent, DialogTitle, Card, DialogActions } from '@mui/material';
import { DocumentTableCardComponent } from 'components/common/documentLM/DocumentTableCardComponent';
import { equipmentApi } from '../api';
import { useEquipmentIvdrOptions } from '../hooks';

export const Equipments = () => {
  const translationPrefix = 'pages.equipment.list';
  const { t } = useTranslation();
  const datagridRefresh = useRef(null);
  const groups = useAppSelector((state) => state.auth.groups);
  const [open, setOpen] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);
  const [equipmentCode, setEquipmentCode] = useState<string>('');
  const [equipmentDescription, setEquipmentDescription] = useState<string>('');
  const [equipmentSerialNumber, setEquipmentSerialNumber] = useState<string>('');
  const { ivdrOptions } = useEquipmentIvdrOptions();

  type EquipmentRow = {
    id: number;
    code?: string;
    description?: string;
    serial_number?: string;
  };

  const handleOpen = (row: EquipmentRow) => {
    setObjectId(row.id);
    setEquipmentCode(row.code ?? '');
    setEquipmentDescription(row.description ?? '');
    setEquipmentSerialNumber(row.serial_number ?? '');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setObjectId(null);
    setEquipmentCode('');
    setEquipmentDescription('');
    setEquipmentSerialNumber('');
  };

  const isDisabledEdit =
    isTechnicianCoordinator() || isTechnician() || isDirector() || isSupplier();

  const columns = EquipmentColumns(
    // ivdrOptions.map((ivdrOption) => ivdrOption.display_name),
    datagridRefresh.current,
    NotOperatorRoles.some((role) => groups.includes(role)),
    handleOpen
  );

  return (
    <>
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        menuRight={
          <IsTenantAdministrator>
            <div className="flex justify-end items-center">
              <ButtonGroup>
                <Link to="new">
                  <Button variant="outlined" size="small">
                    <AddIcon />
                    {t(`${translationPrefix}.add`)}
                  </Button>
                </Link>
              </ButtonGroup>
              <PurgeButton service={equipmentApi.listService} refresh={datagridRefresh} />
              <PurgeLinksButton service={equipmentApi.listService} refresh={datagridRefresh} />
            </div>
          </IsTenantAdministrator>
        }>
        <div className="mt-6">
          <EnhancedDataGrid
            columns={columns}
            service={equipmentApi.listService}
            refresh={datagridRefresh}
            disableEdit={isDisabledEdit}
          />
        </div>
        <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
          <DialogTitle>{t(`${translationPrefix}.view-documents`)}</DialogTitle>
          <DialogContent>
            <Card variant="outlined" sx={{ mt: 2 }}>
              <DocumentTableCardComponent
                originalService="equipment"
                objectId={objectId}
                dictParams={{ equipment: objectId }}
                folder={`menu.equipments/${equipmentCode}`}
                folder_description={`Descrizione: ${equipmentDescription}; Seriale: ${equipmentSerialNumber}; `}
                disableEdit={false}
              />
            </Card>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('global.cancel')}</Button>
          </DialogActions>
        </Dialog>
      </TenantPage>
    </>
  );
};

export default Equipments;
